import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Stack, Chip, Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, Alert, IconButton, Tooltip, Button, Autocomplete, TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon from "@mui/icons-material/Link";
import StorageIcon from "@mui/icons-material/Storage";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import JoinReview from "./JoinReview";

const API = process.env.REACT_APP_API_URL || "http://localhost:8002";

const SCHEMA_COLOR = {
  AIDATALOOKS_DW: "#2563eb", "C##AIDATALOOKS_ERP": "#0891b2", "C##AIDATALOOKS_HCM": "#7c3aed",
  "C##AIDATALOOKS_SCM": "#059669", "C##AIDATALOOKS_CX": "#d97706",
};
const labelOf = (s) => s.replace(/^C##/, "");

function AddJoinForm({ onAdded }) {
  const [tables, setTables] = useState([]);
  const [lt, setLt] = useState(""); const [lc, setLc] = useState("");
  const [rt, setRt] = useState(""); const [rc, setRc] = useState("");
  const [lcols, setLcols] = useState([]); const [rcols, setRcols] = useState([]);
  const [verify, setVerify] = useState(null); const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/oracle/tables`).then(r => r.json())
      .then(d => setTables((d.tables || []).map(t => t.name))).catch(() => {});
  }, []);
  const loadCols = (t, set) => {
    if (!t) return set([]);
    fetch(`${API}/api/oracle/columns/${t}`).then(r => r.json())
      .then(d => set([...(d.measures || []), ...(d.dimensions || [])].map(c => c.name))).catch(() => set([]));
  };
  useEffect(() => { loadCols(lt, setLcols); }, [lt]);
  useEffect(() => { loadCols(rt, setRcols); }, [rt]);

  const doVerify = async () => {
    setBusy(true); setVerify(null);
    try {
      const r = await fetch(`${API}/api/oracle/joins/fdi_multidomain/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ left_table: lt, left_col: lc, right_table: rt, right_col: rc }),
      });
      setVerify(await r.json());
    } catch { setVerify({ verdict: "error" }); }
    setBusy(false);
  };
  const doAdd = async () => {
    await fetch(`${API}/api/oracle/joins/fdi_multidomain/add-extra`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ left_table: lt, left_col: lc, right_table: rt, right_col: rc }),
    });
    setLt(""); setRt(""); setLc(""); setRc(""); setVerify(null);
    onAdded && onAdded();
  };
  const ac = (opts, val, setv, label) => (
    <Autocomplete freeSolo size="small" options={opts} sx={{ minWidth: 150 }}
      value={val} inputValue={val} onInputChange={(e, v) => setv((v || "").toUpperCase())}
      renderInput={p => <TextField {...p} label={label} />} />
  );
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: "#fafbff" }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <AddIcon fontSize="small" sx={{ color: "#1565c0" }} />
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Add a join to this schema</Typography>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        {ac(tables, lt, setLt, "Left table")}{ac(lcols, lc, setLc, "Left col")}
        <Typography>=</Typography>
        {ac(tables, rt, setRt, "Right table")}{ac(rcols, rc, setRc, "Right col")}
        <Button size="small" variant="outlined" disabled={busy || !lc || !rc} onClick={doVerify}>
          {busy ? "…" : "Verify"}
        </Button>
        {verify && (
          <Chip size="small" color={verify.verdict === "valid" ? "success" : verify.verdict === "fanout" || verify.verdict === "low_match" ? "warning" : "error"}
            label={`${verify.verdict} ${verify.ratio != null ? Math.round(verify.ratio * 100) + "%" : ""}`} />
        )}
        {verify && verify.verdict !== "invalid" && verify.verdict !== "error" && (
          <Button size="small" variant="contained" onClick={doAdd}>Add</Button>
        )}
      </Stack>
    </Paper>
  );
}

function FdiPanelBody({ panel, reload }) {
  const joins = panel.confirmed || [];
  const extras = panel.user_added || [];
  const delExtra = async (j) => {
    await fetch(`${API}/api/oracle/joins/fdi_multidomain/delete-join`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(j),
    });
    reload();
  };
  const row = (j, i, deletable) => (
    <Stack key={i} direction="row" alignItems="center" spacing={1}
      sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: i % 2 ? "#fff" : "#f6f8fc" }}>
      <CheckCircleIcon sx={{ color: "#66bb6a", fontSize: 16 }} />
      <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, flex: 1 }}>
        {j.left_table}.{j.left_col} = {j.right_table}.{j.right_col}
      </Typography>
      {j.cross_schema && (
        <Tooltip title={`Cross-schema → ${j.right_schema}`}>
          <Chip size="small" icon={<LinkIcon sx={{ fontSize: 13 }} />} label={j.right_schema}
            sx={{ height: 18, fontSize: 9.5, bgcolor: "#ede7f6", color: "#5e35b1" }} />
        </Tooltip>
      )}
      <Chip size="small" label={`${Math.round((j.ratio || 1) * 100)}%`} sx={{ height: 18 }} />
      {deletable
        ? <IconButton size="small" onClick={() => delExtra(j)}><DeleteIcon sx={{ fontSize: 16, color: "#c62828" }} /></IconButton>
        : <Tooltip title="Verified join (locked)"><Chip size="small" variant="outlined" label="verified" sx={{ height: 18, fontSize: 9.5 }} /></Tooltip>}
    </Stack>
  );
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: "#777", mb: 1 }}>
        {joins.length} verified joins active ({joins.filter(j => j.cross_schema).length} cross-schema).
      </Typography>
      {joins.map((j, i) => row(j, i, false))}
      {extras.length > 0 && (
        <>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1565c0", mt: 1.5, mb: 0.5 }}>
            Your added joins ({extras.length})
          </Typography>
          {extras.map((j, i) => row(j, i, true))}
        </>
      )}
      <AddJoinForm onAdded={reload} />
    </Box>
  );
}

export default function ReviewJoinsMulti() {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/oracle/joins/_grouped/all`)
      .then(r => r.json())
      .then(d => { setPanels(d.panels || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };
  useEffect(load, []);

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="warning">{error}</Alert></Box>;

  return (
    <Box sx={{ bgcolor: "#eceff1", minHeight: "100vh", py: 3 }}>
      <Box sx={{ maxWidth: 920, mx: "auto", px: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Review Joins — all schemas</Typography>
        <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
          Each schema is its own panel. Auto-detected joins are data-verified and active; add or delete joins,
          and approve. Cross-schema (between-pillar) joins are tagged.
        </Typography>

        {panels.map((p) => {
          const color = SCHEMA_COLOR[p.schema] || "#2563eb";
          const total = (p.confirmed?.length || 0) + (p.user_added?.length || 0);
          const cross = (p.confirmed || []).filter(j => j.cross_schema).length;
          return (
            <Accordion key={p.schema} defaultExpanded={p.connection_id === "oracle_free"}
              sx={{ mb: 1, borderLeft: `4px solid ${color}`, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                  <StorageIcon sx={{ color, fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, fontFamily: "monospace", fontSize: 13.5 }}>
                    {labelOf(p.schema)}
                  </Typography>
                  {p.pillar && <Chip size="small" label={p.pillar} sx={{ height: 18, fontSize: 10, bgcolor: color, color: "#fff" }} />}
                  <Box sx={{ flex: 1 }} />
                  <Chip size="small" label={`${total} joins`} sx={{ height: 20, bgcolor: "#e3f2fd", color: "#1565c0" }} />
                  {cross > 0 && <Chip size="small" icon={<LinkIcon sx={{ fontSize: 13 }} />} label={`${cross} cross-schema`}
                    sx={{ height: 20, bgcolor: "#ede7f6", color: "#5e35b1" }} />}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#fff" }}>
                {p.connection_id === "oracle_free"
                  ? <JoinReview connectionId="oracle_free" />
                  : <FdiPanelBody panel={p} reload={load} />}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}
